import React from "react";
import classNames from "classnames";
import { usePrefersReducedMotion } from "./skeletonMotion";

// UX-6a A1: the house skeleton kit. Brand-warm (ivory-2 fill, gentle pulse — NOT
// gray-on-gray) and geometry-first: every skeleton is shaped to match the final
// layout it stands in for, so content arrival causes ZERO layout shift. These are
// for LOADING only — error/empty stay the honest branded Phase-B surfaces.

// The atomic tinted block. Shimmer via pulse, dropped under prefers-reduced-motion.
export function SkeletonBlock({ className, rounded = "rounded-md" }) {
  const reduced = usePrefersReducedMotion();
  return (
    <div
      aria-hidden="true"
      data-testid="skeleton-block"
      className={classNames("bg-ivory-2", rounded, reduced ? "" : "animate-pulse", className)}
    />
  );
}

export function SkeletonLine({ width = "w-full", height = "h-3", className }) {
  return <SkeletonBlock className={classNames(height, width, className)} />;
}

// A panel-shaped card: a heading line + body lines, matching a Panel's rhythm.
export function SkeletonCard({ lines = 3, className }) {
  return (
    <div
      data-testid="skeleton-card"
      className={classNames("rounded-2xl border border-slate-200 bg-white p-4", className)}
    >
      <SkeletonLine width="w-1/3" height="h-4" />
      <div className="mt-3 space-y-2">
        {Array.from({ length: lines }).map((_, index) => (
          <SkeletonLine key={index} width={index === lines - 1 ? "w-2/3" : "w-full"} />
        ))}
      </div>
    </div>
  );
}

// A row of stat "doorways" — matches the PriorityStrip / snapshot-card grids.
export function SkeletonStatRow({ count = 4, className }) {
  return (
    <div data-testid="skeleton-stat-row" className={classNames("grid grid-cols-1 gap-4 sm:grid-cols-2", className)}>
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="min-h-[120px] rounded-l-none rounded-r-2xl border border-l-4 border-slate-200 bg-white p-4">
          <SkeletonLine width="w-1/2" height="h-4" />
          <SkeletonLine width="w-12" height="h-8" className="mt-4" />
        </div>
      ))}
    </div>
  );
}

// A chart placeholder — a titled panel with a chart-height block.
export function SkeletonChart({ height = "h-56", className }) {
  return (
    <div data-testid="skeleton-chart" className={classNames("rounded-2xl border border-slate-200 bg-white p-4", className)}>
      <SkeletonLine width="w-40" height="h-4" />
      <SkeletonBlock className={classNames("mt-4 w-full", height)} rounded="rounded-xl" />
    </div>
  );
}

// UX-6a A4: the route-transition shell. A page-shaped skeleton (title + subtitle
// + stat row + list) used as the lazy-route Suspense fallback so navigation paints
// the destination's layout immediately — never a dead frame or a gray table. One
// generic page shell (the common admin/teacher page geometry), not per page.
export function RouteShellSkeleton({ className }) {
  return (
    <div
      data-testid="route-shell-skeleton"
      className={classNames("mx-auto max-w-6xl space-y-6 px-4 py-5 sm:px-6 sm:py-6", className)}
    >
      <div className="space-y-2">
        <SkeletonLine width="w-56" height="h-6" />
        <SkeletonLine width="w-80" />
      </div>
      <SkeletonStatRow count={4} />
      <SkeletonListRows rows={4} />
    </div>
  );
}

// A stack of list rows — matches feeds / lesson lists / table bodies.
export function SkeletonListRows({ rows = 4, className }) {
  return (
    <div data-testid="skeleton-list-rows" className={classNames("space-y-3", className)}>
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="rounded-md border border-slate-200 bg-white p-4">
          <div className="flex items-center justify-between gap-3">
            <SkeletonLine width="w-40" height="h-4" />
            <SkeletonLine width="w-16" height="h-3" />
          </div>
          <SkeletonLine width="w-2/3" className="mt-3" />
        </div>
      ))}
    </div>
  );
}
